import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize, forkJoin, of } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import {
  EquipmentCategory, EquipmentResource, FieldCapability, FieldEquipmentAllocation, FieldEquipmentRequirement,
  FieldLaborAssignment, FieldLaborCandidate, FieldLaborRequirement, FieldLaborRequirementSkill,
  FieldLaborRoleOption, FieldProficiencyLevel, FieldWorkPackage
} from './field-work.models';
import { FieldWorkService } from './field-work.service';

type AssignmentBillingUnit = 'HOUR' | 'DAY' | 'TASK' | 'FLAT';
interface AssignmentPriceOverride {
  enabled: boolean;
  buyRate?: number;
  sellRate?: number;
  billingUnit?: AssignmentBillingUnit;
  currencyCode?: string;
}

const ACTIVITY_ROLE_KEYWORDS: Record<string, string[]> = {
  GENERAL: ['GENERAL_WORKER', 'FIELD_HELPER', 'HELPER'],
  LOADING: ['LOADER', 'LOADING', 'MATERIAL_HANDLER', 'HANDLER', 'FIELD_HELPER', 'HELPER', 'FORKLIFT_OPERATOR'],
  UNLOADING: ['UNLOADER', 'UNLOADING', 'MATERIAL_HANDLER', 'HANDLER', 'FIELD_HELPER', 'HELPER', 'FORKLIFT_OPERATOR'],
  DELIVERY: ['DELIVERY_AGENT', 'DELIVERY', 'DRIVER', 'COURIER', 'FIELD_HELPER', 'HELPER'],
  INSTALLATION: ['INSTALLER', 'INSTALLATION', 'TECHNICIAN', 'MERCHANDISER', 'FIELD_HELPER', 'HELPER'],
  REMOVAL: ['REMOVAL', 'DISMANTLER', 'INSTALLER', 'TECHNICIAN', 'FIELD_HELPER', 'HELPER'],
  RETURN: ['RETURN_AGENT', 'RETURN', 'DRIVER', 'COURIER', 'FIELD_HELPER', 'HELPER'],
  SITE_SUPPORT: ['SITE_SUPERVISOR', 'SITE_SUPPORT', 'TEAM_LEAD', 'TECHNICIAN', 'FIELD_HELPER', 'HELPER'],
  HANDLING: ['MATERIAL_HANDLER', 'HANDLER', 'FIELD_HELPER', 'HELPER', 'FORKLIFT_OPERATOR'],
  INSPECTION: ['INSPECTOR', 'INSPECTION', 'QUALITY', 'QA', 'SITE_SUPERVISOR'],
  STUFFING: ['STUFFING', 'LOADER', 'MATERIAL_HANDLER', 'FIELD_HELPER', 'HELPER'],
  DESTUFFING: ['DESTUFFING', 'UNLOADER', 'MATERIAL_HANDLER', 'FIELD_HELPER', 'HELPER'],
  RECEIVING: ['RECEIVER', 'RECEIVING', 'WAREHOUSE_OPERATOR', 'MATERIAL_HANDLER', 'FIELD_HELPER'],
  PICKING: ['PICKER', 'PICKING', 'WAREHOUSE_OPERATOR', 'FIELD_HELPER'],
  PACKING: ['PACKER', 'PACKING', 'WAREHOUSE_OPERATOR', 'FIELD_HELPER'],
  PUTAWAY: ['PUTAWAY', 'WAREHOUSE_OPERATOR', 'FORKLIFT_OPERATOR', 'FIELD_HELPER'],
  DOCUMENTATION: ['DOCUMENTATION', 'DOCUMENT_CONTROLLER', 'CLERK', 'COORDINATOR'],
  CLEARANCE_SUPPORT: ['CUSTOMS_CLEARANCE', 'CLEARANCE', 'CUSTOMS_BROKER', 'DOCUMENTATION', 'COORDINATOR']
};

@Component({
  selector: 'app-field-work-labor-planning-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TagModule
  ],
  templateUrl: './field-work-labor-planning-dialog.component.html',
  styleUrls: ['./field-work-labor-planning-dialog.component.css']
})
export class FieldWorkLaborPlanningDialogComponent implements OnChanges {
  private readonly service = inject(FieldWorkService);

  @Input() visible = false;
  @Input() work?: FieldWorkPackage;
  @Input() canManage = false;
  @Input() canAssign = false;
  @Input() canRelease = false;
  @Input() canViewEquipment = false;
  @Input() canManageEquipment = false;
  @Input() canOverride = false;
  @Input() canViewLaborRates = false;
  @Input() canViewEquipmentRates = false;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() changed = new EventEmitter<FieldWorkPackage>();

  readonly currentWork = signal<FieldWorkPackage | undefined>(undefined);
  readonly roles = signal<FieldLaborRoleOption[]>([]);
  readonly capabilities = signal<FieldCapability[]>([]);
  readonly proficiencyLevels = signal<FieldProficiencyLevel[]>([]);
  readonly categories = signal<EquipmentCategory[]>([]);
  readonly selectedRoleId = signal<number | null>(null);
  readonly showAllRoles = signal(false);
  readonly headcount = signal(1);
  readonly selectedCapabilityId = signal<number | null>(null);
  readonly capabilityRequirementType = signal<'MANDATORY' | 'PREFERRED'>('MANDATORY');
  readonly minimumProficiencyId = signal<number | null>(null);
  readonly draftSkills = signal<FieldLaborRequirementSkill[]>([]);
  readonly activeRequirementId = signal<number | null>(null);
  readonly candidates = signal<FieldLaborCandidate[]>([]);
  readonly selectedCandidateIds = signal<number[]>([]);
  readonly selectedEquipmentCategoryId = signal<number | null>(null);
  readonly equipmentQuantity = signal(1);
  readonly equipmentRequirementType = signal<'MANDATORY' | 'PREFERRED'>('MANDATORY');
  readonly activeEquipmentRequirementId = signal<number | null>(null);
  readonly equipmentCandidates = signal<EquipmentResource[]>([]);
  readonly loadingSetup = signal(false);
  readonly savingRequirement = signal(false);
  readonly removingRequirementId = signal<number | null>(null);
  readonly savingEquipmentRequirement = signal(false);
  readonly loadingCandidates = signal(false);
  readonly loadingEquipmentCandidates = signal(false);
  readonly assigningSelection = signal(false);
  readonly reservingResourceId = signal<number | null>(null);
  readonly transitioning = signal(false);
  readonly error = signal('');
  overrideReasons: Record<string, string> = {};
  laborPriceOverrides: Record<string, AssignmentPriceOverride> = {};
  equipmentPriceOverrides: Record<string, AssignmentPriceOverride> = {};
  readonly billingUnitOptions = ['HOUR', 'DAY', 'TASK', 'FLAT'];

  readonly allRoleOptions = computed(() => this.roles()
    .filter(role => Boolean(role.fieldRoleId && (role.roleName || role.roleCode)))
    .map(role => {
      const roleName = role.roleName || role.roleCode || `Role ${role.fieldRoleId}`;
      return {
        ...role,
        roleName,
        roleCode: role.roleCode || `ROLE-${role.fieldRoleId}`,
        label: `${roleName}${role.workerCategory ? ` — ${this.label(role.workerCategory)}` : ''}`
      };
    }));

  readonly recommendedRoleOptions = computed(() => this.allRoleOptions()
    .map(role => ({ ...role, activityScore: this.activityRoleScore(role) }))
    .filter(role => role.activityScore > 0)
    .sort((left, right) => right.activityScore - left.activityScore
      || left.roleName.localeCompare(right.roleName)));

  readonly roleOptions = computed(() => {
    const recommended = this.recommendedRoleOptions();
    const recommendedIds = new Set(recommended.map(role => role.fieldRoleId));
    const withRecommendation = (role: typeof recommended[number]) => ({
      ...role,
      label: `${role.label}${recommendedIds.has(role.fieldRoleId) ? ' · Recommended' : ''}`
    });
    if (!recommended.length || this.showAllRoles()) {
      return [
        ...recommended.map(withRecommendation),
        ...this.allRoleOptions()
          .filter(role => !recommendedIds.has(role.fieldRoleId))
          .sort((left, right) => left.roleName.localeCompare(right.roleName))
      ];
    }
    return recommended.map(withRecommendation);
  });

  readonly capabilityOptions = computed(() => this.capabilities()
    .filter(capability => Boolean(capability.fieldCapabilityId && capability.capabilityName))
    .map(capability => ({
      ...capability,
      label: `${capability.capabilityName}${capability.capabilityCategory ? ` — ${capability.capabilityCategory}` : ''}`
    })));

  readonly activeRequirement = computed(() => {
    const id = this.activeRequirementId();
    return this.currentWork()?.laborRequirements?.find(requirement => requirement.fieldLaborRequirementId === id);
  });

  readonly remainingLaborSlots = computed(() => {
    const requirement = this.activeRequirement();
    return requirement ? Math.max(0, (requirement.headcount || 0) - this.assignedFor(requirement)) : 0;
  });

  readonly selectedCandidates = computed(() => {
    const selected = new Set(this.selectedCandidateIds());
    return this.candidates().filter(candidate => candidate.fieldWorkerId != null
      && selected.has(candidate.fieldWorkerId));
  });

  readonly activeEquipmentRequirement = computed(() => {
    const id = this.activeEquipmentRequirementId();
    return this.currentWork()?.equipmentRequirements?.find(
      requirement => requirement.fieldEquipmentRequirementId === id);
  });

  readonly hasRequirements = computed(() => Boolean(
    this.currentWork()?.laborRequirements?.length || this.currentWork()?.equipmentRequirements?.length
  ));

  readonly laborCovered = computed(() => (this.currentWork()?.laborRequirements || []).every(requirement =>
    this.assignedFor(requirement) >= (requirement.headcount || 0)));

  readonly laborCoverage = computed(() => {
    const requirements = this.currentWork()?.laborRequirements || [];
    const required = requirements.reduce((sum, requirement) => sum + (requirement.headcount || 0), 0);
    const assigned = requirements.reduce((sum, requirement) => sum + this.assignedFor(requirement), 0);
    return { assigned, required };
  });

  readonly equipmentCoverage = computed(() => {
    const requirements = (this.currentWork()?.equipmentRequirements || [])
      .filter(requirement => requirement.requirementType !== 'PREFERRED');
    const required = requirements.reduce((sum, requirement) => sum + (requirement.requiredQuantity || 0), 0);
    const allocated = requirements.reduce((sum, requirement) => sum + this.allocatedFor(requirement), 0);
    return { allocated, required };
  });

  readonly equipmentCovered = computed(() => (this.currentWork()?.equipmentRequirements || [])
    .filter(requirement => requirement.requirementType !== 'PREFERRED')
    .every(requirement => this.allocatedFor(requirement) >= (requirement.requiredQuantity || 0)));

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['work'] || changes['visible']?.currentValue === true) {
      this.currentWork.set(this.work);
      this.error.set('');
      this.activeRequirementId.set(null);
      this.candidates.set([]);
      this.selectedCandidateIds.set([]);
      this.activeEquipmentRequirementId.set(null);
      this.equipmentCandidates.set([]);
      this.overrideReasons = {};
      this.laborPriceOverrides = {};
      this.equipmentPriceOverrides = {};
      this.resetRoleForm();
      this.resetEquipmentForm();
    }
    if (changes['visible']?.currentValue === true) this.loadSetup();
  }

  get status(): string {
    return this.currentWork()?.status || 'DRAFT';
  }

  get nextActionTitle(): string {
    if (this.status === 'DRAFT' && !this.hasRequirements()) return 'Add required resources';
    if (this.status === 'DRAFT') return 'Plan the package';
    if (this.status === 'PLANNED' && this.currentWork()?.requirementOnly) return 'Requirements ready for propagation';
    if (this.status === 'PLANNED' && !this.laborCovered()) return 'Fill every labor role';
    if (this.status === 'PLANNED' && !this.equipmentCovered()) return 'Reserve mandatory equipment';
    if (this.status === 'PLANNED') return 'Review blockers and release';
    if (this.status === 'RELEASED') return 'Begin execution';
    if (this.status === 'IN_PROGRESS') return 'Record and approve actual work';
    return 'Review the completed work and finance links';
  }

  get nextActionText(): string {
    if (this.status === 'DRAFT' && !this.hasRequirements()) {
      return 'Add the people and reusable equipment this activity needs. Capabilities define who is eligible for each role.';
    }
    if (this.status === 'DRAFT') {
      return this.currentWork()?.requirementOnly
        ? 'Planning confirms the requirements so they can propagate into transport execution.'
        : 'Planning confirms the resource requirements. Candidate assignment and reservation become available next.';
    }
    if (this.status === 'PLANNED' && this.currentWork()?.requirementOnly) {
      return 'No field agents or tools are assigned here. They become assignable after the order release enters truck or bulk planning, and remain linked through shipment delivery.';
    }
    if (this.status === 'PLANNED' && !this.laborCovered()) {
      return 'Open each role, review eligibility and conflicts, then assign enough people to meet its headcount.';
    }
    if (this.status === 'PLANNED' && !this.equipmentCovered()) {
      return 'Reserve enough available resources to cover every mandatory equipment requirement.';
    }
    if (this.status === 'PLANNED') {
      return 'Mandatory labor and equipment coverage is complete. Resolve remaining blockers before release.';
    }
    if (this.status === 'RELEASED') {
      return 'The plan and rates are confirmed. Start the package when the field team begins work.';
    }
    if (this.status === 'IN_PROGRESS') {
      return 'Capture labor actuals, equipment usage and evidence, then submit them for supervisor approval.';
    }
    return 'Approved actuals retain traceability to costs, payables and any billable receivables.';
  }

  close(): void {
    if (!this.busy()) this.visibleChange.emit(false);
  }

  busy(): boolean {
    return this.savingRequirement() || this.savingEquipmentRequirement() || this.loadingCandidates()
      || this.loadingEquipmentCandidates() || this.assigningSelection()
      || this.removingRequirementId() != null || this.reservingResourceId() != null || this.transitioning();
  }

  resetRoleForm(): void {
    this.selectedRoleId.set(null);
    this.showAllRoles.set(false);
    this.headcount.set(1);
    this.selectedCapabilityId.set(null);
    this.capabilityRequirementType.set('MANDATORY');
    this.minimumProficiencyId.set(null);
    this.draftSkills.set([]);
    this.selectRecommendedRole();
  }

  get activityRoleHint(): string {
    const activity = this.label(this.currentWork()?.activityType || 'GENERAL');
    const count = this.recommendedRoleOptions().length;
    if (!count) return `No configured role directly matches ${activity}; showing the full role catalog.`;
    return `${count} role${count === 1 ? '' : 's'} suggested for ${activity}. The best match is selected automatically.`;
  }

  toggleRoleScope(): void {
    this.showAllRoles.update(value => !value);
    if (!this.showAllRoles()) this.selectRecommendedRole();
  }

  resetEquipmentForm(): void {
    this.selectedEquipmentCategoryId.set(null);
    this.equipmentQuantity.set(1);
    this.equipmentRequirementType.set('MANDATORY');
  }

  addCapabilityToDraft(): void {
    const capability = this.capabilities().find(
      candidate => candidate.fieldCapabilityId === this.selectedCapabilityId());
    if (!capability?.fieldCapabilityId) {
      this.error.set('Select a Field Work capability.');
      return;
    }
    if (this.draftSkills().some(skill => skill.fieldCapabilityId === capability.fieldCapabilityId)) {
      this.error.set('That capability is already attached to this role.');
      return;
    }
    const proficiency = this.proficiencyLevels().find(
      level => level.fieldProficiencyLevelId === this.minimumProficiencyId());
    this.draftSkills.update(skills => [...skills, {
      fieldCapabilityId: capability.fieldCapabilityId,
      skillName: capability.capabilityName,
      requirementType: this.capabilityRequirementType(),
      minimumFieldProficiencyLevelId: proficiency?.fieldProficiencyLevelId,
      minimumProficiencyLevel: proficiency?.levelName,
      minimumProficiencyRank: proficiency?.levelRank
    }]);
    this.selectedCapabilityId.set(null);
    this.minimumProficiencyId.set(null);
    this.error.set('');
  }

  removeDraftCapability(fieldCapabilityId?: number): void {
    this.draftSkills.update(skills => skills.filter(skill => skill.fieldCapabilityId !== fieldCapabilityId));
  }

  addRole(): void {
    const work = this.currentWork();
    const role = this.roles().find(candidate => candidate.fieldRoleId === this.selectedRoleId());
    if (!work?.fieldWorkPackageId || !role) {
      this.error.set('Select a role from the Field Work catalog.');
      return;
    }
    this.savingRequirement.set(true);
    this.error.set('');
    this.service.saveLaborRequirement(work.fieldWorkPackageId, {
      fieldRoleId: role.fieldRoleId,
      roleCode: role.roleCode || `ROLE-${role.fieldRoleId}`,
      roleName: role.roleName || role.roleCode,
      headcount: Math.max(1, this.headcount() || 1),
      skills: this.draftSkills()
    }).pipe(finalize(() => this.savingRequirement.set(false))).subscribe({
      next: () => {
        this.resetRoleForm();
        this.refresh();
      },
      error: error => this.error.set(this.errorMessage(error))
    });
  }

  removeRole(requirement: FieldLaborRequirement): void {
    const work = this.currentWork();
    const requirementId = requirement.fieldLaborRequirementId;
    if (!work?.fieldWorkPackageId || !requirementId) return;
    if (!this.canManage || !['DRAFT', 'PLANNED'].includes(this.status)) {
      this.error.set('Roles can be removed only while the package is draft or planned.');
      return;
    }
    const assigned = this.assignmentsFor(requirement).length;
    const assignmentText = assigned
      ? ` This will also remove ${assigned} current assignment${assigned === 1 ? '' : 's'}.`
      : '';
    if (!window.confirm(`Remove the ${requirement.roleName || 'selected'} role?${assignmentText}`)) return;

    this.removingRequirementId.set(requirementId);
    this.error.set('');
    this.service.removeLaborRequirement(work.fieldWorkPackageId, requirementId)
      .pipe(finalize(() => this.removingRequirementId.set(null)))
      .subscribe({
        next: () => {
          if (this.activeRequirementId() === requirementId) {
            this.activeRequirementId.set(null);
            this.candidates.set([]);
            this.selectedCandidateIds.set([]);
          }
          this.refresh();
        },
        error: error => this.error.set(this.errorMessage(error))
      });
  }

  showCandidates(requirement: FieldLaborRequirement): void {
    const work = this.currentWork();
    if (!work?.fieldWorkPackageId || !requirement.fieldLaborRequirementId) return;
    if (!work.assignmentAllowed) {
      this.error.set('Assignments are available only in truck planning, bulk planning, or shipment delivery.');
      return;
    }
    if (this.status === 'DRAFT') {
      this.error.set('Plan the package before assigning people.');
      return;
    }
    this.activeRequirementId.set(requirement.fieldLaborRequirementId);
    this.selectedCandidateIds.set([]);
    this.fetchCandidates(work.fieldWorkPackageId, requirement.fieldLaborRequirementId);
  }

  isCandidateSelected(candidate: FieldLaborCandidate): boolean {
    return candidate.fieldWorkerId != null && this.selectedCandidateIds().includes(candidate.fieldWorkerId);
  }

  canSelectCandidate(candidate: FieldLaborCandidate): boolean {
    return this.isCandidateSelected(candidate)
      || (this.remainingLaborSlots() > this.selectedCandidateIds().length && this.canAssignCandidate(candidate));
  }

  toggleCandidate(candidate: FieldLaborCandidate): void {
    const workerId = candidate.fieldWorkerId;
    if (!workerId || !this.canSelectCandidate(candidate)) return;
    if (!candidate.eligible && !this.overrideReasons[this.laborOverrideKey(candidate)]?.trim()) {
      this.error.set('Enter an audited override reason before selecting this blocked candidate.');
      return;
    }
    if (this.candidateNeedsRateOverride(candidate) && !this.validLaborPriceOverride(candidate)) {
      this.error.set('Enter an assignment price override and audited reason for this candidate.');
      return;
    }
    this.error.set('');
    this.selectedCandidateIds.update(selected => selected.includes(workerId)
      ? selected.filter(id => id !== workerId)
      : [...selected, workerId]);
  }

  assignmentSelectionReady(): boolean {
    const selected = this.selectedCandidates();
    return selected.length > 0
      && selected.length <= this.remainingLaborSlots()
      && selected.every(candidate => (candidate.eligible
        || Boolean(this.overrideReasons[this.laborOverrideKey(candidate)]?.trim()))
        && (!this.candidateNeedsRateOverride(candidate) || this.validLaborPriceOverride(candidate))
        && (!this.laborPriceOverride(candidate)?.enabled || this.validLaborPriceOverride(candidate)));
  }

  assignSelected(): void {
    const work = this.currentWork();
    const requirementId = this.activeRequirementId();
    const selected = this.selectedCandidates();
    if (!work?.fieldWorkPackageId || !requirementId || !this.assignmentSelectionReady()) return;

    const requests = selected.map(candidate => {
      const overrideReason = this.overrideReasons[this.laborOverrideKey(candidate)]?.trim();
      const priceOverride = this.laborPriceOverride(candidate);
      const assignment: FieldLaborAssignment = {
        fieldWorkerId: candidate.fieldWorkerId,
        assignmentStatus: 'CONFIRMED',
        plannedStart: String(work.plannedStart),
        plannedEnd: String(work.plannedEnd),
        plannedQuantity: 1,
        buyRateSnapshot: priceOverride?.enabled ? priceOverride.buyRate : undefined,
        sellRateSnapshot: priceOverride?.enabled ? priceOverride.sellRate : undefined,
        billingUnit: priceOverride?.enabled ? priceOverride.billingUnit : undefined,
        currencyCode: priceOverride?.enabled ? priceOverride.currencyCode?.trim().toUpperCase() : undefined,
        overrideReason: (!candidate.eligible || priceOverride?.enabled) ? overrideReason : undefined
      };
      return this.service.assignLabor(work.fieldWorkPackageId!, requirementId, assignment);
    });

    this.assigningSelection.set(true);
    this.error.set('');
    forkJoin(requests).pipe(finalize(() => this.assigningSelection.set(false))).subscribe({
      next: () => {
        this.selectedCandidateIds.set([]);
        this.refresh(true);
      },
      error: error => this.error.set(this.errorMessage(error))
    });
  }

  addEquipmentRequirement(): void {
    const work = this.currentWork();
    const category = this.categories().find(
      candidate => candidate.equipmentCategoryId === this.selectedEquipmentCategoryId());
    if (!work?.fieldWorkPackageId || !category?.equipmentCategoryId) {
      this.error.set('Select an equipment category.');
      return;
    }
    this.savingEquipmentRequirement.set(true);
    this.error.set('');
    this.service.saveEquipmentRequirement(work.fieldWorkPackageId, {
      equipmentCategoryId: category.equipmentCategoryId,
      categoryName: category.categoryName,
      requiredQuantity: Math.max(1, this.equipmentQuantity() || 1),
      requirementType: this.equipmentRequirementType(),
      plannedStart: work.plannedStart,
      plannedEnd: work.plannedEnd,
      locationId: work.locationId,
      locationName: work.locationName
    }).pipe(finalize(() => this.savingEquipmentRequirement.set(false))).subscribe({
      next: () => {
        this.resetEquipmentForm();
        this.refresh();
      },
      error: error => this.error.set(this.errorMessage(error))
    });
  }

  showEquipmentCandidates(requirement: FieldEquipmentRequirement): void {
    const work = this.currentWork();
    if (!work?.fieldWorkPackageId || !requirement.fieldEquipmentRequirementId) return;
    if (!work.assignmentAllowed) {
      this.error.set('Equipment reservation is available only in truck planning, bulk planning, or shipment delivery.');
      return;
    }
    if (this.status === 'DRAFT') {
      this.error.set('Plan the package before reserving equipment.');
      return;
    }
    this.activeEquipmentRequirementId.set(requirement.fieldEquipmentRequirementId);
    this.fetchEquipmentCandidates(work.fieldWorkPackageId, requirement.fieldEquipmentRequirementId);
  }

  reserveEquipment(candidate: EquipmentResource): void {
    const work = this.currentWork();
    const requirement = this.activeEquipmentRequirement();
    if (!work?.fieldWorkPackageId || !requirement?.fieldEquipmentRequirementId
      || !candidate.equipmentResourceId || !this.canReserveCandidate(candidate)) return;
    const overrideReason = this.overrideReasons[this.equipmentOverrideKey(candidate)]?.trim();
    const priceOverride = this.equipmentPriceOverride(candidate);
    if (!candidate.available && !overrideReason) {
      this.error.set('Enter an audited override reason before reserving this resource.');
      return;
    }
    if (priceOverride?.enabled && !this.validEquipmentPriceOverride(candidate)) {
      this.error.set('Enter an equipment price override and audited reason before reserving.');
      return;
    }
    const remaining = Math.max(1, (requirement.requiredQuantity || 1) - this.allocatedFor(requirement));
    const allocation: FieldEquipmentAllocation = {
      equipmentResourceId: candidate.equipmentResourceId,
      plannedStart: work.plannedStart,
      plannedEnd: work.plannedEnd,
      plannedQuantity: candidate.trackingMode === 'SERIALIZED' ? 1 : remaining,
      buyRateSnapshot: priceOverride?.enabled ? priceOverride.buyRate : undefined,
      sellRateSnapshot: priceOverride?.enabled ? priceOverride.sellRate : undefined,
      billingUnit: priceOverride?.enabled ? priceOverride.billingUnit : undefined,
      currencyCode: priceOverride?.enabled ? priceOverride.currencyCode?.trim().toUpperCase() : undefined,
      overrideReason: (!candidate.available || priceOverride?.enabled) ? overrideReason : undefined
    };
    this.reservingResourceId.set(candidate.equipmentResourceId);
    this.error.set('');
    this.service.allocateEquipment(work.fieldWorkPackageId, requirement.fieldEquipmentRequirementId, allocation)
      .pipe(finalize(() => this.reservingResourceId.set(null)))
      .subscribe({
        next: () => this.refresh(false, true),
        error: error => this.error.set(this.errorMessage(error))
      });
  }

  transition(status: 'PLANNED' | 'RELEASED'): void {
    const work = this.currentWork();
    if (!work?.fieldWorkPackageId) return;
    if (status === 'PLANNED' && !this.hasRequirements()) {
      this.error.set('Add at least one labor or equipment requirement before planning.');
      return;
    }
    this.transitioning.set(true);
    this.error.set('');
    this.service.transition(work.fieldWorkPackageId, status)
      .pipe(finalize(() => this.transitioning.set(false)))
      .subscribe({
        next: () => this.refresh(),
        error: error => this.error.set(this.errorMessage(error))
      });
  }

  assignedFor(requirement: FieldLaborRequirement): number {
    return (this.currentWork()?.laborAssignments || []).filter(assignment =>
      assignment.fieldLaborRequirementId === requirement.fieldLaborRequirementId
      && !['CANCELLED', 'REJECTED'].includes(assignment.assignmentStatus || '')).length;
  }

  assignmentsFor(requirement: FieldLaborRequirement): FieldLaborAssignment[] {
    return (this.currentWork()?.laborAssignments || []).filter(assignment =>
      assignment.fieldLaborRequirementId === requirement.fieldLaborRequirementId
      && !['CANCELLED', 'REJECTED'].includes(assignment.assignmentStatus || ''));
  }

  allocatedFor(requirement: FieldEquipmentRequirement): number {
    if (requirement.allocatedQuantity != null) return requirement.allocatedQuantity;
    return this.allocationsFor(requirement).reduce((sum, allocation) => sum + (allocation.plannedQuantity || 0), 0);
  }

  allocationsFor(requirement: FieldEquipmentRequirement): FieldEquipmentAllocation[] {
    return (this.currentWork()?.equipmentAllocations || []).filter(allocation =>
      allocation.fieldEquipmentRequirementId === requirement.fieldEquipmentRequirementId
      && !['CANCELLED', 'RETURNED'].includes(allocation.allocationStatus || ''));
  }

  requirementSkills(requirement: FieldLaborRequirement): string {
    return (requirement.skills || []).map(skill =>
      `${skill.skillName}${skill.minimumProficiencyLevel ? ` (${skill.minimumProficiencyLevel})` : ''}`).join(', ');
  }

  candidateMessage(candidate: FieldLaborCandidate): string {
    if (candidate.blockers?.length) return candidate.blockers.join(' — ');
    if (candidate.warnings?.length) return candidate.warnings.join(' — ');
    return 'Meets the role, capability, availability and financial checks.';
  }

  equipmentCandidateMessage(candidate: EquipmentResource): string {
    if (candidate.blockers?.length) return candidate.blockers.join(' — ');
    if (candidate.warnings?.length) return candidate.warnings.join(' — ');
    return 'Available for the planned interval and location.';
  }

  canAssignCandidate(candidate: FieldLaborCandidate): boolean {
    if (candidate.eligible) return true;
    if (!this.canOverride || !(candidate.blockers?.length)) return false;
    return !candidate.blockers.some(blocker => /^(IDENTITY|FINANCE):/.test(blocker));
  }

  canReserveCandidate(candidate: EquipmentResource): boolean {
    if (candidate.available) return true;
    if (!this.canOverride || !(candidate.blockers?.length)) return false;
    return !candidate.blockers.some(blocker => blocker.startsWith('HARD:'));
  }

  laborOverrideKey(candidate: FieldLaborCandidate): string {
    return `labor-${candidate.fieldWorkerId}`;
  }

  equipmentOverrideKey(candidate: EquipmentResource): string {
    return `equipment-${candidate.equipmentResourceId}`;
  }

  toggleLaborPriceOverride(candidate: FieldLaborCandidate): void {
    if (!this.canOverride || !this.canViewLaborRates) return;
    const key = this.laborOverrideKey(candidate);
    const existing = this.laborPriceOverrides[key];
    this.laborPriceOverrides[key] = existing?.enabled ? { ...existing, enabled: false } : {
      enabled: true,
      buyRate: existing?.buyRate ?? candidate.resolvedRate?.buyRate,
      sellRate: existing?.sellRate ?? candidate.resolvedRate?.sellRate,
      billingUnit: existing?.billingUnit ?? candidate.resolvedRate?.billingUnit ?? 'HOUR',
      currencyCode: existing?.currencyCode ?? candidate.resolvedRate?.currencyCode ?? 'SAR'
    };
  }

  toggleEquipmentPriceOverride(candidate: EquipmentResource): void {
    if (!this.canOverride || !this.canViewEquipmentRates) return;
    const key = this.equipmentOverrideKey(candidate);
    const existing = this.equipmentPriceOverrides[key];
    this.equipmentPriceOverrides[key] = existing?.enabled ? { ...existing, enabled: false } : {
      enabled: true,
      buyRate: existing?.buyRate,
      sellRate: existing?.sellRate,
      billingUnit: existing?.billingUnit ?? 'TASK',
      currencyCode: existing?.currencyCode ?? 'SAR'
    };
  }

  laborPriceOverride(candidate: FieldLaborCandidate): AssignmentPriceOverride | undefined {
    return this.laborPriceOverrides[this.laborOverrideKey(candidate)];
  }

  equipmentPriceOverride(candidate: EquipmentResource): AssignmentPriceOverride | undefined {
    return this.equipmentPriceOverrides[this.equipmentOverrideKey(candidate)];
  }

  candidateNeedsRateOverride(candidate: FieldLaborCandidate): boolean {
    return Boolean(candidate.blockers?.some(blocker => blocker.startsWith('RATE:')));
  }

  validLaborPriceOverride(candidate: FieldLaborCandidate): boolean {
    const override = this.laborPriceOverride(candidate);
    return Boolean(this.canOverride && this.canViewLaborRates && override?.enabled
      && (override.buyRate != null || override.sellRate != null)
      && override.billingUnit && override.currencyCode?.trim()
      && this.overrideReasons[this.laborOverrideKey(candidate)]?.trim());
  }

  validEquipmentPriceOverride(candidate: EquipmentResource): boolean {
    const override = this.equipmentPriceOverride(candidate);
    return Boolean(this.canOverride && this.canViewEquipmentRates && override?.enabled
      && (override.buyRate != null || override.sellRate != null)
      && override.billingUnit && override.currencyCode?.trim()
      && this.overrideReasons[this.equipmentOverrideKey(candidate)]?.trim());
  }

  severity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    if (['RELEASED', 'IN_PROGRESS', 'COMPLETED'].includes(status || '')) return 'success';
    if (['DRAFT', 'PLANNED'].includes(status || '')) return 'warn';
    if (status === 'CANCELLED') return 'danger';
    return 'info';
  }

  private loadSetup(): void {
    this.loadingSetup.set(true);
    forkJoin({
      roles: this.service.laborRoles(),
      capabilities: this.service.capabilities(),
      proficiencyLevels: this.service.proficiencyLevels(),
      categories: this.canViewEquipment ? this.service.categories() : of([] as EquipmentCategory[])
    }).pipe(finalize(() => this.loadingSetup.set(false))).subscribe({
      next: setup => {
        this.roles.set(setup.roles);
        this.capabilities.set(setup.capabilities);
        this.proficiencyLevels.set(setup.proficiencyLevels);
        this.categories.set(setup.categories);
        this.selectRecommendedRole();
      },
      error: error => {
        this.roles.set([]);
        this.capabilities.set([]);
        this.proficiencyLevels.set([]);
        this.categories.set([]);
        this.error.set(this.errorMessage(error));
      }
    });
  }

  private refresh(refreshCandidates = false, refreshEquipmentCandidates = false): void {
    const packageId = this.currentWork()?.fieldWorkPackageId;
    if (!packageId) return;
    this.service.packageById(packageId).subscribe({
      next: work => {
        this.currentWork.set(work);
        this.changed.emit(work);
        const requirementId = this.activeRequirementId();
        if (refreshCandidates && requirementId) this.fetchCandidates(packageId, requirementId);
        const equipmentRequirementId = this.activeEquipmentRequirementId();
        if (refreshEquipmentCandidates && equipmentRequirementId) {
          this.fetchEquipmentCandidates(packageId, equipmentRequirementId);
        }
      },
      error: error => this.error.set(this.errorMessage(error))
    });
  }

  private fetchCandidates(packageId: number, requirementId: number): void {
    this.loadingCandidates.set(true);
    this.error.set('');
    this.service.laborCandidates(packageId, requirementId)
      .pipe(finalize(() => this.loadingCandidates.set(false)))
      .subscribe({
        next: candidates => this.candidates.set(candidates),
        error: error => this.error.set(this.errorMessage(error))
      });
  }

  private fetchEquipmentCandidates(packageId: number, requirementId: number): void {
    this.loadingEquipmentCandidates.set(true);
    this.error.set('');
    this.service.equipmentCandidates(packageId, requirementId)
      .pipe(finalize(() => this.loadingEquipmentCandidates.set(false)))
      .subscribe({
        next: candidates => this.equipmentCandidates.set(candidates),
        error: error => this.error.set(this.errorMessage(error))
      });
  }

  private label(value?: string): string {
    return (value || '').replaceAll('_', ' ').toLowerCase().replace(/\b\w/g, character => character.toUpperCase());
  }

  private selectRecommendedRole(): void {
    if (this.selectedRoleId()) return;
    const existingRoleIds = new Set((this.currentWork()?.laborRequirements || [])
      .map(requirement => requirement.fieldRoleId)
      .filter((roleId): roleId is number => roleId != null));
    const recommendation = this.recommendedRoleOptions()
      .find(role => role.fieldRoleId != null && !existingRoleIds.has(role.fieldRoleId));
    this.selectedRoleId.set(recommendation?.fieldRoleId ?? null);
  }

  private activityRoleScore(role: FieldLaborRoleOption): number {
    const activity = this.normalizeMatchValue(this.currentWork()?.activityType || 'GENERAL');
    const keywords = ACTIVITY_ROLE_KEYWORDS[activity] || [activity];
    const identity = `_${this.normalizeMatchValue([role.roleCode, role.roleName].filter(Boolean).join('_'))}_`;
    const category = `_${this.normalizeMatchValue(role.workerCategory || '')}_`;
    return keywords.reduce((best, keyword, index) => {
      const normalizedKeyword = this.normalizeMatchValue(keyword);
      if (!normalizedKeyword) return best;
      const weight = keywords.length - index;
      if (identity.includes(`_${normalizedKeyword}_`)) return Math.max(best, 100 + weight);
      if (category.includes(`_${normalizedKeyword}_`)) return Math.max(best, 10 + weight);
      return best;
    }, 0);
  }

  private normalizeMatchValue(value: string): string {
    return value.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  private errorMessage(error: any): string {
    return error?.error?.message || error?.error?.error || error?.message || 'Field work planning action failed.';
  }
}
